/**
 * The play / pause / prev / next cluster, and nothing about layout.
 *
 * The cluster owns its control sizes and its glyphs; where it sits, what it
 * sits next to and how much room it gets are the holder's business. That split
 * is the point: the previous player baked a 44px circle, a timeline and a
 * status line into one span, so a poster that wanted only the play control had
 * to reimplement it.
 *
 * Two rules are load-bearing. Prev and next exist ONLY when the caller hands
 * over a handler — a disabled ghost tells a reader there is a next track when
 * there is not, and a reader cannot tell a disabled control from a broken one.
 * And there is no shuffle and no repeat: neither has any meaning in a
 * projection of thirty-second previews, so neither is offered.
 *
 * The play/pause glyph crossfades rather than swapping, because both glyphs
 * occupy the same box and a hard swap at 44px reads as a flicker.
 *
 * Callers mount the transport only when they have playable media. A failed
 * attempt returns to a live play control so it can be retried.
 *
 * `children` mount inside the primary control's own well, layered with the
 * glyphs. That is where an equaliser belongs — a wordless statement that sound
 * is moving, in the control that is moving it.
 */
"use client";
import {
  useCallback,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconPlayerSkipBackFilled,
  IconPlayerSkipForwardFilled,
} from "@tabler/icons-react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import {
  crcTransition,
  CrcAnimatedProperty,
  CrcDuration,
  CrcEase,
} from "./cresultcard.motion";
import {
  CRC_ACCENT,
  CRC_R_PILL,
  CRC_TEXT_MUTED,
  CRC_TOUCH_TARGET,
} from "./cresultcard.tokens";
import {
  CresultcardPlaybackStatus,
  CresultcardTransportScale,
} from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";
import "./Cresultcard.css";

const QUIET_HOVER_CLASS_NAME = "cresultcard__quiet-hover";
const CONTROL_CURSOR = "pointer";
/** Neighbours are unlabelled by default rather than unlabelled full stop. */
const PREVIOUS_LABEL_FALLBACK = "Previous track";
const NEXT_LABEL_FALLBACK = "Next track";

interface TransportMetrics {
  /** The primary control's box at Wide. Mobile takes the touch floor instead. */
  readonly primary: number;
  readonly primaryGlyph: number;
  readonly neighbour: number;
  readonly neighbourGlyph: number;
  readonly gap: number;
}

/**
 * `Icon` is the bare 44px control the spec names — no surrounding chrome, sized
 * at the touch floor on every viewport because it is the whole affordance.
 * `Inline` sits inside a dense row and `Block` anchors a card or a detail.
 */
const TRANSPORT_METRICS: Record<CresultcardTransportScale, TransportMetrics> = {
  [CresultcardTransportScale.Icon]: {
    primary: CRC_TOUCH_TARGET,
    primaryGlyph: 17,
    neighbour: CRC_TOUCH_TARGET,
    neighbourGlyph: 14,
    gap: 2,
  },
  [CresultcardTransportScale.Inline]: {
    primary: 32,
    primaryGlyph: 13,
    neighbour: 28,
    neighbourGlyph: 11,
    gap: 2,
  },
  [CresultcardTransportScale.Block]: {
    primary: 52,
    primaryGlyph: 21,
    neighbour: 40,
    neighbourGlyph: 16,
    gap: 6,
  },
};

const ROOT_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  minWidth: 0,
  flexShrink: 0,
};

const GLYPH_TRANSITION = crcTransition({
  properties: [CrcAnimatedProperty.Opacity],
  duration: CrcDuration.Tap,
  ease: CrcEase.Settle,
});

interface ControlStyleInput {
  readonly size: number;
  readonly color: string;
}

function controlStyle({ size, color }: ControlStyleInput): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: size,
    height: size,
    borderRadius: CRC_R_PILL,
    // `disableHoverEffects` hands hover paint to the kit's own class, and it
    // also resets the cursor — which has to be put back here.
    cursor: CONTROL_CURSOR,
    color,
  };
}

/** The accessible names. Neighbour names are only read when a neighbour exists. */
export interface CresultcardTransportLabels {
  readonly play: string;
  readonly pause: string;
  readonly previous?: string;
  readonly next?: string;
}

export interface CresultcardTransportProps {
  readonly status: CresultcardPlaybackStatus;
  readonly scale: CresultcardTransportScale;
  readonly onPlay: () => void;
  readonly onPause: () => void;
  /** Declared ONLY when a real previous neighbour exists. */
  readonly onPrevious?: () => void;
  /** Declared ONLY when a real next neighbour exists. */
  readonly onNext?: () => void;
  /** Mounted inside the primary control's well — an equaliser, and nothing else. */
  readonly children?: ReactNode;
  readonly labels: CresultcardTransportLabels;
}

export function CresultcardTransport({
  status,
  scale,
  onPlay,
  onPause,
  onPrevious,
  onNext,
  children,
  labels,
}: CresultcardTransportProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const metrics = TRANSPORT_METRICS[scale];
  const primarySize = isMobile
    ? Math.max(metrics.primary, CRC_TOUCH_TARGET)
    : metrics.primary;
  const neighbourSize = isMobile
    ? Math.max(metrics.neighbour, CRC_TOUCH_TARGET)
    : metrics.neighbour;

  /**
   * Buffering is still "the reader asked for sound", so the primary control
   * shows pause and stops it. A play glyph during a load invites a second tap
   * on something that is already loading.
   */
  const isStopping =
    status === CresultcardPlaybackStatus.Playing ||
    status === CresultcardPlaybackStatus.Buffering;
  const primaryLabel = isStopping ? labels.pause : labels.play;
  const pressPrimary = useCallback((): void => {
    if (isStopping) {
      onPause();
      return;
    }
    onPlay();
  }, [isStopping, onPause, onPlay]);

  const glyphBoxStyle: CSSProperties = {
    position: "relative",
    display: "inline-flex",
    width: metrics.primaryGlyph,
    height: metrics.primaryGlyph,
  };
  const glyphLayerStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    display: "inline-flex",
    transition: GLYPH_TRANSITION,
  };

  return (
    <div
      data-el="Cresultcard/Transport/Root"
      style={{ ...ROOT_STYLE, gap: metrics.gap }}
    >
      {onPrevious != null && (
        <Cbutton
          ariaLabel={labels.previous ?? PREVIOUS_LABEL_FALLBACK}
          disableClickMotion
          disableHoverEffects
          onClick={onPrevious}
          padding={0}
          preset={ButtonPreset.Ghost}
          radius={CRC_R_PILL}
          rootAttributes={{
            "data-el": "Cresultcard/Transport/PreviousTrigger",
            "data-crc-control": true,
          }}
          rootClassName={QUIET_HOVER_CLASS_NAME}
          style={controlStyle({ size: neighbourSize, color: CRC_TEXT_MUTED })}
        >
          <IconPlayerSkipBackFilled size={metrics.neighbourGlyph} />
        </Cbutton>
      )}

      <Cbutton
        ariaLabel={primaryLabel}
        disableClickMotion
        disableHoverEffects
        onClick={pressPrimary}
        padding={0}
        preset={ButtonPreset.Ghost}
        radius={CRC_R_PILL}
        rootAttributes={{
          "data-el": "Cresultcard/Transport/PlayTrigger",
          "data-crc-control": true,
        }}
        rootClassName={QUIET_HOVER_CLASS_NAME}
        style={controlStyle({ size: primarySize, color: CRC_ACCENT })}
      >
        <span
          aria-hidden
          style={glyphBoxStyle}
        >
          <span
            style={{ ...glyphLayerStyle, opacity: isStopping ? 0 : 1 }}
          >
            <IconPlayerPlayFilled size={metrics.primaryGlyph} />
          </span>
          <span
            style={{ ...glyphLayerStyle, opacity: isStopping ? 1 : 0 }}
          >
            <IconPlayerPauseFilled size={metrics.primaryGlyph} />
          </span>
          {children != null && (
            <span
              style={{
                ...glyphLayerStyle,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {children}
            </span>
          )}
        </span>
      </Cbutton>

      {onNext != null && (
        <Cbutton
          ariaLabel={labels.next ?? NEXT_LABEL_FALLBACK}
          disableClickMotion
          disableHoverEffects
          onClick={onNext}
          padding={0}
          preset={ButtonPreset.Ghost}
          radius={CRC_R_PILL}
          rootAttributes={{
            "data-el": "Cresultcard/Transport/NextTrigger",
            "data-crc-control": true,
          }}
          rootClassName={QUIET_HOVER_CLASS_NAME}
          style={controlStyle({ size: neighbourSize, color: CRC_TEXT_MUTED })}
        >
          <IconPlayerSkipForwardFilled size={metrics.neighbourGlyph} />
        </Cbutton>
      )}
    </div>
  );
}
